// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { getHelpDocPopUpById } from "../services/helpDocPopup.service";
import { showHelpDoc } from "../store/auth/action";
import { userLogsAPI } from "../services/session.service";
import { getSession } from "../utils/sessionstore";
import { logAmplitudeEvent } from "../utils/amplitude/functions";
import amplitude from "../utils/amplitude/initiate";

// Same trigger logic Header.jsx uses for its Help Guide button (handleHelpGuideClick),
// extracted so other entry points (e.g. the Help and Support panel) can open the
// same HelpGuide component/PDF popup without duplicating the API/analytics wiring.
export function useHelpGuide({ triggerType = "HEADER" } = {}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { clientID } = useParams();
  const pathParts = location.pathname.split("/");
  const clientid = getSession("client_id");

  const { activedashboard, userDetails, clientname, subscriptions } = useSelector((state) => ({
    activedashboard: state.activedashboard,
    userDetails: state.userDetails,
    clientname: state.clientname,
    subscriptions: state.subscriptions,
  }));

  const [helpObj, setHelpObj] = useState([]);
  const [open, setOpen] = useState(false);
  const [productLine, setProductLine] = useState("");
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (!subscriptions?.length || !activedashboard?._id) return;
    for (const subscription of subscriptions) {
      const categories = subscription.productcategoryIds || [];
      for (const category of categories) {
        const dashboards = category.dashboardIds || [];
        const match = dashboards.find((dash) => dash.standarddashboardId?._id === activedashboard.standarddashboardId?._id);
        if (match) {
          setProductLine(subscription.label);
          break;
        }
      }
    }
  }, [subscriptions, activedashboard]);

  const isAvailable = Boolean(
    pathParts.includes("dashboard") && activedashboard?.helpDoc && activedashboard?.helpDoc?.length > 0
  );

  function prefetch() {
    if (!isAvailable || !activedashboard?.helpDoc?.includes("popupId-")) return;
    const popupId = activedashboard.helpDoc.split("-")[1];
    getHelpDocPopUpById(popupId).catch(() => {});
  }

  async function getHelpDocPopUpByIdAPI(id) {
    try {
      const response = await getHelpDocPopUpById(id);
      setHelpObj(response || []);
      setOpen(true);
      return response;
    } catch (e) {
      console.log(e);
    }
  }

  async function callUserLogAPIForHelpGuide(startTime, endTime) {
    try {
      await userLogsAPI(
        "USER_GUIDE",
        activedashboard?.standarddashboardId?.standardName || "",
        userDetails,
        clientname,
        activedashboard?.dashboardname || "",
        "Viewed",
        clientid,
        activedashboard?._id || "",
        startTime,
        endTime
      );
    } catch (err) {
      console.log(err.message);
    }
  }

  async function sectionLog({ chapter = "", section = "" }) {
    try {
      logAmplitudeEvent(amplitude, "Help Guide Section", {
        productLine,
        dashboardName: activedashboard?.dashboardname ?? "",
        standardDashboardName: activedashboard?.standarddashboardId?.standardName ?? "",
        dashboardId: activedashboard?._id ?? "",
        clientName: clientname,
        clientId: clientID ?? clientid,
        triggerType: "HELP GUIDE SECTION",
        targetType: activedashboard?.helpDoc?.includes("popupId-") ? "POPUP" : "PDF",
        chapter,
        section,
        eventTimestamp: new Date().toLocaleString("en-US", { timeZone: "UTC" }),
      });
      await userLogsAPI(
        "USER_GUIDE",
        (activedashboard?.standarddashboardId?.standardName || "") + " - " + chapter + " - " + section,
        userDetails,
        clientname,
        activedashboard?.dashboardname || "",
        "Viewed",
        clientid,
        activedashboard?._id || ""
      );
    } catch (err) {
      console.log(err.message);
    }
  }

  async function chapterLog({ chapter = "" }) {
    try {
      logAmplitudeEvent(amplitude, "Help Guide Chapter", {
        productLine,
        dashboardName: activedashboard?.dashboardname ?? "",
        standardDashboardName: activedashboard?.standarddashboardId?.standardName ?? "",
        dashboardId: activedashboard?._id ?? "",
        clientName: clientname,
        clientId: clientID ?? clientid,
        triggerType,
        targetType: activedashboard?.helpDoc?.includes("popupId-") ? "POPUP" : "PDF",
        chapter,
        eventTimestamp: new Date().toLocaleString("en-US", { timeZone: "UTC" }),
      });
      await userLogsAPI(
        "USER_GUIDE",
        (activedashboard?.standarddashboardId?.standardName || "") + " - " + chapter,
        userDetails,
        clientname,
        activedashboard?.dashboardname || "",
        "Viewed",
        clientid,
        activedashboard?._id || ""
      );
    } catch (err) {
      console.log(err.message);
    }
  }

  function openHelpGuide() {
    if (!isAvailable) {
      console.warn("[HelpGuide] Not opened - isAvailable=false", {
        pathname: location.pathname,
        onDashboardRoute: pathParts.includes("dashboard"),
        helpDoc: activedashboard?.helpDoc,
      });
      return;
    }
    logAmplitudeEvent(amplitude, "Help Guide", {
      productLine,
      dashboardName: activedashboard?.dashboardname ?? "",
      standardDashboardName: activedashboard?.standarddashboardId?.standardName ?? "",
      dashboardId: activedashboard?._id ?? "",
      clientName: clientname,
      clientId: clientID ?? clientid,
      triggerType,
      targetType: activedashboard?.helpDoc.includes("popupId-") ? "POPUP" : "PDF",
      eventTimestamp: new Date().toLocaleString("en-US", { timeZone: "UTC" }),
    });
    if (activedashboard?.helpDoc.includes("popupId-")) {
      const popupId = activedashboard?.helpDoc.split("-")[1];
      getHelpDocPopUpByIdAPI(popupId);
    } else {
      dispatch(showHelpDoc());
    }
  }

  useEffect(() => {
    function handleSessionStart() {
      startTimeRef.current = new Date();
    }
    function handleSessionEnd() {
      if (!startTimeRef.current || activedashboard?.dashboardname === undefined) return;
      const endTime = new Date();
      callUserLogAPIForHelpGuide(startTimeRef.current, endTime);
      startTimeRef.current = null;
    }
    const handleBeforeUnload = (event) => {
      handleSessionEnd();
      event.preventDefault();
      event.returnValue = "";
    };

    if (open) handleSessionStart();
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      handleSessionEnd();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [open]);

  return {
    open,
    setOpen,
    helpObj,
    setHelpObj,
    isAvailable,
    openHelpGuide,
    prefetch,
    sectionLog,
    chapterLog,
  };
}
